<div class="vc-dashboard vc-view-shell">
  <main class="vc-dashboard__layout vc-view-content">
    <section class="vc-dashboard__frame vc-surface-panel">
      <div class="vc-dashboard__toolbar vc-page-toolbar">
        <div class="vc-dashboard__period-controls">
          <div class="vc-dashboard__preset-group" role="group">
            @for (option of presets; track option.value) {
              <button
                type="button"
                class="vc-dashboard__preset"
                [class.vc-dashboard__preset--active]="preset === option.value"
                (click)="setPreset(option.value)"
              >
                {{ option.labelKey | translate }}
              </button>
            }
          </div>
          <div class="vc-dashboard__period-navigation">
            @if (preset !== 'all') {
              <button
                type="button"
                class="vc-dashboard__icon-button"
                (click)="shiftPeriod(-1)"
                [attr.aria-label]="'DASHBOARD_OP.FILTERS.PREVIOUS' | translate"
              >
                <i class="pi pi-angle-left"></i>
              </button>
            }
            <button
              type="button"
              class="vc-dashboard__date-button"
              (click)="goToToday()"
            >
              <i class="pi pi-calendar"></i>
              <span>
                {{
                  data
                    ? formatDate(data.range.dateFrom, true) +
                      " — " +
                      formatDate(data.range.dateTo, true)
                    : "—"
                }}
              </span>
            </button>
            @if (preset !== 'all') {
              <button
                type="button"
                class="vc-dashboard__icon-button"
                (click)="shiftPeriod(1)"
                [attr.aria-label]="'DASHBOARD_OP.FILTERS.NEXT' | translate"
              >
                <i class="pi pi-angle-right"></i>
              </button>
            }
          </div>
        </div>
        <button
          type="button"
          class="vc-dashboard__refresh"
          (click)="load()"
          [disabled]="loading"
        >
          <i class="pi pi-refresh" [class.vc-dashboard__spin]="loading"></i>
          <span>{{ "DASHBOARD_OP.ACTIONS.REFRESH" | translate }}</span>
        </button>
      </div>

      <div class="vc-dashboard__content">
        @if (loading && !data) {
          <div class="vc-dashboard__state">
            <i class="pi pi-spin pi-spinner"></i
            >{{ "DASHBOARD_OP.STATE.LOADING" | translate }}
          </div>
        } @else if (loadError && !data) {
          <div class="vc-dashboard__state vc-dashboard__state--error">
            <i class="pi pi-exclamation-triangle"></i
            >{{ "DASHBOARD_OP.STATE.ERROR" | translate }}
            <button type="button" (click)="load()">
              {{ "DASHBOARD_OP.ACTIONS.RETRY" | translate }}
            </button>
          </div>
        } @else if (data) {
          <section
            class="vc-dashboard__activity-kpis"
            [class.vc-dashboard__activity-kpis--without-comparison]="!hasComparison"
            [attr.aria-label]="'DASHBOARD_OVERVIEW.KPIS.ACTIVITY' | translate"
          >
            <article class="vc-dashboard__activity-kpi vc-dashboard__activity-kpi--blue">
              <span>{{ "DASHBOARD_OVERVIEW.KPIS.APPOINTMENTS" | translate }}</span>
              <strong>{{ formatNumber(data.activity.appointments.value) }}</strong>
              <small class="vc-dashboard__kpi-comparison">
                <span>{{
                  "DASHBOARD_OVERVIEW.KPIS.PREVIOUS"
                    | translate
                      : {
                          value: formatNumber(data.activity.appointments.previousValue),
                        }
                }}</span>
                @if (hasComparison && data.activity.appointments.change !== null) {
                  <strong [ngClass]="metricClass(data.activity.appointments)">{{ formatChange(data.activity.appointments.change) }}</strong>
                }
              </small>
            </article>
            <article class="vc-dashboard__activity-kpi vc-dashboard__activity-kpi--blue">
              <span>{{ "DASHBOARD_OVERVIEW.KPIS.PATIENTS" | translate }}</span>
              <strong>{{ formatNumber(data.activity.patients.value) }}</strong>
              <small class="vc-dashboard__kpi-comparison">
                <span>{{
                  "DASHBOARD_OVERVIEW.KPIS.PREVIOUS"
                    | translate
                      : {
                          value: formatNumber(data.activity.patients.previousValue),
                        }
                }}</span>
                @if (hasComparison && data.activity.patients.change !== null) {
                  <strong [ngClass]="metricClass(data.activity.patients)">{{ formatChange(data.activity.patients.change) }}</strong>
                }
              </small>
            </article>
            <article class="vc-dashboard__activity-kpi vc-dashboard__activity-kpi--blue">
              <span>{{ "DASHBOARD_OVERVIEW.KPIS.WORKED_HOURS" | translate }}</span>
              <strong>{{ formatNumber(data.activity.workedHours.value) }} h</strong>
              <small class="vc-dashboard__kpi-comparison">
                <span>{{ "DASHBOARD_OVERVIEW.KPIS.PREVIOUS" | translate : { value: formatNumber(data.activity.workedHours.previousValue) + ' h' } }}</span>
                @if (hasComparison && data.activity.workedHours.change !== null) {
                  <strong [ngClass]="metricClass(data.activity.workedHours)">{{ formatChange(data.activity.workedHours.change) }}</strong>
                }
              </small>
            </article>
            <article class="vc-dashboard__activity-kpi vc-dashboard__activity-kpi--blue">
              <span>{{ "DASHBOARD_OVERVIEW.KPIS.NEW_PATIENTS" | translate }}</span>
              <strong>{{ formatNumber(data.activity.newPatients.value) }}</strong>
              <small class="vc-dashboard__kpi-comparison">
                <span>{{ "DASHBOARD_OVERVIEW.KPIS.PREVIOUS" | translate : { value: formatNumber(data.activity.newPatients.previousValue) } }}</span>
                @if (hasComparison && data.activity.newPatients.change !== null) {
                  <strong [ngClass]="metricClass(data.activity.newPatients)">{{ formatChange(data.activity.newPatients.change) }}</strong>
                }
              </small>
            </article>
            <article class="vc-dashboard__activity-kpi vc-dashboard__activity-kpi--danger">
              <span>{{ "DASHBOARD_OVERVIEW.KPIS.CANCELLATIONS" | translate }}</span>
              <strong>{{ formatNumber(data.cancellations.count.value) }}</strong>
              <small class="vc-dashboard__kpi-comparison">
                <span>{{ "DASHBOARD_OVERVIEW.KPIS.PREVIOUS" | translate : { value: formatNumber(data.cancellations.count.previousValue) } }}</span>
                @if (hasComparison && data.cancellations.count.change !== null) {
                  <strong [ngClass]="inverseChangeClass(data.cancellations.count.change)">{{ formatChange(data.cancellations.count.change) }}</strong>
                }
              </small>
            </article>
            <article class="vc-dashboard__activity-kpi vc-dashboard__activity-kpi--income">
              <span>{{ "DASHBOARD_OVERVIEW.KPIS.INCOME_PER_HOUR" | translate }}</span>
              <strong>{{ formatMoney(data.activity.incomePerWorkedHour.value) }}</strong>
              <small class="vc-dashboard__kpi-comparison">
                <span>{{ "DASHBOARD_OVERVIEW.KPIS.PREVIOUS" | translate : { value: formatMoney(data.activity.incomePerWorkedHour.previousValue) } }}</span>
                @if (hasComparison && data.activity.incomePerWorkedHour.change !== null) {
                  <strong [ngClass]="metricClass(data.activity.incomePerWorkedHour)">{{ formatChange(data.activity.incomePerWorkedHour.change) }}</strong>
                }
              </small>
            </article>
          </section>

          <section class="vc-dashboard__overview-grid">
            <article class="vc-dashboard__kpi vc-dashboard__kpi--economic">
              <header class="vc-dashboard__kpi-header"><i class="pi pi-wallet"></i><span>{{ "DASHBOARD_OVERVIEW.ECONOMIC.TITLE" | translate }}</span></header>
              @if (data.economic; as economic) {
                <div class="vc-dashboard__kpi-primary" [title]="'DASHBOARD_OVERVIEW.ECONOMIC.GENERATED_HELP' | translate">
                  <small>{{ (data.monthlyForecast ? "DASHBOARD_OVERVIEW.ECONOMIC.GENERATED_TO_DATE" : "DASHBOARD_OVERVIEW.ECONOMIC.GENERATED_PERIOD") | translate }}</small>
                  <strong>{{ formatMoney(economic.generated) }}</strong>
                </div>
                <div class="vc-dashboard__economic-values">
                  <span [title]="'DASHBOARD_OVERVIEW.ECONOMIC.COLLECTED_HELP' | translate"><small>{{ "DASHBOARD_OVERVIEW.ECONOMIC.COLLECTED" | translate }}</small><strong>{{ formatMoney(economic.collected) }}</strong></span>
                  <span><small>{{ "DASHBOARD_OVERVIEW.ECONOMIC.PENDING" | translate }}</small><strong>{{ formatMoney(economic.pending) }}</strong></span>
                  <span><small>{{ "DASHBOARD_OVERVIEW.ECONOMIC.PENDING_APPOINTMENTS" | translate }}</small><strong>{{ formatNumber(economic.pendingAppointments) }}</strong></span>
                </div>
                <div class="vc-dashboard__progress"><div [style.width.%]="economic.collectedRate || 0"></div></div>
                <small class="vc-dashboard__progress-label">{{ "DASHBOARD_OVERVIEW.ECONOMIC.COLLECTED_RATE" | translate }} · <strong>{{ economic.collectedRate === null ? '—' : formatNumber(economic.collectedRate) + '%' }}</strong></small>
                @if (data.monthlyForecast; as forecast) {
                  <div class="vc-dashboard__forecast-summary">
                    <span><small>{{ "DASHBOARD_OVERVIEW.FORECAST.FUTURE_BOOKED" | translate }}</small><strong>{{ formatMoney(forecast.futureBooked) }}</strong></span>
                    <span><small>{{ "DASHBOARD_OVERVIEW.FORECAST.CLOSING" | translate }}</small><strong>{{ formatMoney(forecast.closingForecast) }}</strong></span>
                  </div>
                }
                @if (data.incidents?.available && economic.pending > 0) {
                  <button type="button" (click)="viewUnpaidIncidents()">{{ "DASHBOARD_OVERVIEW.ECONOMIC.REVIEW_PENDING" | translate }}</button>
                }
              } @else {
                <p class="vc-dashboard__kpi-empty">—</p>
              }
            </article>
            <article class="vc-dashboard__kpi vc-dashboard__kpi--ranking">
              <header class="vc-dashboard__kpi-header"><i class="pi pi-chart-bar"></i><span>{{ "DASHBOARD_OVERVIEW.RANKING.TITLE" | translate }}</span></header>
              <p class="vc-dashboard__kpi-description">{{ "DASHBOARD_OVERVIEW.RANKING.DESCRIPTION" | translate }}</p>
              @if (data.treatmentRanking.items.length) {
                <div class="vc-dashboard__ranking-table">
                  <div class="vc-dashboard__ranking-head">
                    <span>{{ "DASHBOARD_OVERVIEW.RANKING.TREATMENT" | translate }}</span>
                    <button type="button" (click)="setTreatmentSort('appointments')">{{ "DASHBOARD_OVERVIEW.RANKING.APPOINTMENTS" | translate }}</button>
                    <button type="button" (click)="setTreatmentSort('revenue')">{{ "DASHBOARD_OVERVIEW.RANKING.REVENUE" | translate }}</button>
                    <button type="button" (click)="setTreatmentSort('hours')">{{ "DASHBOARD_OVERVIEW.RANKING.HOURS" | translate }}</button>
                    <button type="button" (click)="setTreatmentSort('revenuePerHour')">{{ "DASHBOARD_OVERVIEW.RANKING.REVENUE_PER_HOUR" | translate }}</button>
                  </div>
                  @for (treatment of data.treatmentRanking.items; track treatment.id) {
                    <div class="vc-dashboard__ranking-row">
                      <strong>{{ treatment.name }}</strong><span>{{ formatNumber(treatment.appointments) }}</span><span>{{ formatMoney(treatment.revenue) }}</span><span>{{ formatNumber(treatment.hours) }} h</span><span>{{ formatMoney(treatment.revenuePerHour) }}</span>
                    </div>
                  }
                </div>
                @if (data.treatmentRanking.pagination.totalPages > 1) {
                  <footer class="vc-dashboard__compact-pagination">
                    <button type="button" [disabled]="treatmentsPage <= 1" (click)="changeTreatmentsPage(treatmentsPage - 1)"><i class="pi pi-angle-left"></i></button>
                    <span>{{ "DASHBOARD_OVERVIEW.RANKING.PAGE" | translate : { current: data.treatmentRanking.pagination.page, total: data.treatmentRanking.pagination.totalPages } }}</span>
                    <button type="button" [disabled]="treatmentsPage >= data.treatmentRanking.pagination.totalPages" (click)="changeTreatmentsPage(treatmentsPage + 1)"><i class="pi pi-angle-right"></i></button>
                  </footer>
                }
              } @else {
                <p class="vc-dashboard__kpi-empty">{{ "DASHBOARD_OVERVIEW.RANKING.EMPTY" | translate }}</p>
              }
            </article>
            <article class="vc-dashboard__kpi vc-dashboard__kpi--incidents">
              <header class="vc-dashboard__kpi-header"><i class="pi pi-exclamation-triangle"></i><span>{{ "DASHBOARD_OVERVIEW.INCIDENTS.TITLE" | translate }}</span></header>
              @if (data.incidents?.available) {
                <div class="vc-dashboard__incident-summary">
                  <div><span>{{ "DASHBOARD_OVERVIEW.INCIDENTS.DETECTED" | translate }}</span><strong>{{ formatNumber(data.incidents.detected) }}</strong></div>
                  <div><span>{{ "DASHBOARD_OVERVIEW.INCIDENTS.IGNORED" | translate }}</span><strong>{{ formatNumber(data.incidents.resolved) }}</strong></div>
                  <div><span>{{ "DASHBOARD_OVERVIEW.INCIDENTS.OPEN" | translate }}</span><strong>{{ formatNumber(data.incidents.open) }}</strong></div>
                </div>
                <div class="vc-dashboard__incident-reasons">
                  @for (reason of incidentReasons(data.incidents.reasons); track reason.key) {
                    <div><span>{{ reason.label | translate }}</span><strong>{{ reason.count }}</strong></div>
                  }
                </div>
                <button type="button" (click)="viewDashboardIncidents()">{{ "DASHBOARD_OVERVIEW.INCIDENTS.VIEW" | translate }}</button>
              } @else {
                <p class="vc-dashboard__kpi-empty">{{ "DASHBOARD_OVERVIEW.INCIDENTS.UNAVAILABLE" | translate }}</p>
              }
            </article>
          </section>

          <section class="vc-dashboard__summary-grid">
            <article class="vc-dashboard-card">
              <header class="vc-dashboard-card__header">
                <i class="pi pi-times-circle" aria-hidden="true"></i>
                <div>
                  <h2>
                    {{ "DASHBOARD_OVERVIEW.CANCELLATIONS.TITLE" | translate }}
                  </h2>
                  <p>
                    {{
                      "DASHBOARD_OVERVIEW.CANCELLATIONS.DESCRIPTION" | translate
                    }}
                  </p>
                </div>
              </header>
              <div class="vc-dashboard__summary-values">
                <strong>{{ data.cancellations.count.value }}</strong>
                <span>{{ formatNumber(data.cancellations.rate.value) }}%</span>
                @if (hasComparison) {
                  <small
                    [ngClass]="inverseChangeClass(data.cancellations.rate.change || 0)"
                    >{{ formatChange(data.cancellations.rate.change, " pp") }}</small
                  >
                }
              </div>
              <div class="vc-dashboard__cancellation-details">
                <span><small>{{ "DASHBOARD_OVERVIEW.CANCELLATIONS.REBOOKED" | translate }}</small><strong>{{ formatNumber(data.cancellations.rebooked) }}</strong></span>
                <span><small>{{ "DASHBOARD_OVERVIEW.CANCELLATIONS.WITHOUT_NEW_APPOINTMENT" | translate }}</small><strong>{{ formatNumber(data.cancellations.withoutNewAppointment) }}</strong></span>
                @if (data.cancellations.estimatedImpact !== null) {
                  <span><small>{{ "DASHBOARD_OVERVIEW.CANCELLATIONS.ESTIMATED_IMPACT" | translate }}</small><strong>{{ formatMoney(data.cancellations.estimatedImpact) }}</strong></span>
                }
              </div>
              @if (data.cancellations.recent.length) {
                <div class="vc-dashboard__cancellation-list">
                  @for (item of visibleCancellations; track item.start) {
                    <div>
                      <time class="vc-dashboard__list-meta">{{ formatDate(item.start) }} · {{ formatTime(item.start) }}</time>
                      <strong class="vc-dashboard__list-title">{{ item.patient || ("DASHBOARD_OP.COMMON.NO_PATIENT" | translate) }}</strong>
                      <small class="vc-dashboard__list-meta">{{ item.concept || ("DASHBOARD_OP.COMMON.NO_TREATMENT" | translate) }}</small>
                    </div>
                  }
                </div>
                @if (cancellationsTotalPages > 1) {
                  <footer class="vc-dashboard__compact-pagination">
                    <button type="button" [disabled]="cancellationsPage <= 1" (click)="changeCancellationsPage(cancellationsPage - 1)"><i class="pi pi-angle-left"></i></button>
                    <span>{{ "DASHBOARD_OVERVIEW.CANCELLATIONS.PAGE" | translate : { current: cancellationsPage, total: cancellationsTotalPages } }}</span>
                    <button type="button" [disabled]="cancellationsPage >= cancellationsTotalPages" (click)="changeCancellationsPage(cancellationsPage + 1)"><i class="pi pi-angle-right"></i></button>
                  </footer>
                }
              } @else {
                <div class="vc-dashboard__empty">{{ "DASHBOARD_OVERVIEW.CANCELLATIONS.EMPTY" | translate }}</div>
              }
            </article>

            <article class="vc-dashboard-card">
              <header class="vc-dashboard-card__header">
                <i class="pi pi-calendar-clock" aria-hidden="true"></i>
                <div>
                  <h2>{{ "DASHBOARD_OVERVIEW.OCCUPANCY.TITLE" | translate }}</h2>
                  <p>
                    {{ "DASHBOARD_OVERVIEW.OCCUPANCY.DESCRIPTION" | translate }}
                  </p>
                </div>
              </header>
              <div class="vc-dashboard__summary-values">
                <strong>{{ formatNumber(data.holes.occupancyRate) }}%</strong>
                <span>{{ formatNumber(data.holes.occupiedHours) }} h {{ "DASHBOARD_OVERVIEW.OCCUPANCY.RESERVED" | translate }}</span>
                @if (hasComparison) {
                  <small [ngClass]="data.holes.change > 0 ? 'positive' : data.holes.change < 0 ? 'negative' : 'neutral'">{{ formatChange(data.holes.change, " pp") }}</small>
                }
              </div>
              <div class="vc-dashboard__occupancy-details">
                <span><small>{{ "DASHBOARD_OVERVIEW.OCCUPANCY.AVAILABLE" | translate }}</small><strong>{{ formatNumber(data.holes.plannedHours) }} h</strong></span>
                <span><small>{{ "DASHBOARD_OVERVIEW.OCCUPANCY.FREE" | translate }}</small><strong>{{ formatNumber(data.holes.freeHours) }} h</strong></span>
              </div>
              <div class="vc-dashboard__calendar-list">
                @for (
                  calendar of data.holes.calendars;
                  track calendar.calendar.id
                ) {
                  <div>
                    <span class="vc-dashboard__list-title">{{ calendar.calendar.name }}</span
                    ><strong class="vc-dashboard__list-value">{{ formatNumber(calendar.occupancyRate) }}%</strong
                    ><small class="vc-dashboard__list-meta">{{ formatNumber(calendar.occupiedHours) }} h · {{ formatNumber(calendar.freeHours) }} h {{ "DASHBOARD_OVERVIEW.OCCUPANCY.FREE_SHORT" | translate }}</small>
                  </div>
                } @empty {
                  <div class="vc-dashboard__empty">
                    {{ "DASHBOARD_OVERVIEW.OCCUPANCY.EMPTY" | translate }}
                  </div>
                }
              </div>
            </article>

            <article class="vc-dashboard-card">
              <header class="vc-dashboard-card__header">
                <i class="pi pi-gift" aria-hidden="true"></i>
                <div>
                  <h2>
                    {{ "DASHBOARD_OVERVIEW.BIRTHDAYS.TITLE" | translate }}
                  </h2>
                  <p>
                    {{ "DASHBOARD_OVERVIEW.BIRTHDAYS.DESCRIPTION" | translate }}
                  </p>
                </div>
              </header>
              <div class="vc-dashboard__birthday-list">
                @for (birthday of visibleBirthdays; track birthday.id) {
                  <div>
                    <time class="vc-dashboard__list-meta vc-dashboard__birthday-date">{{ formatDate(birthday.date) }}</time
                    ><app-patient-link class="vc-dashboard__list-title" [patient]="birthday" /><small class="vc-dashboard__list-meta">{{
                      birthdayLabel(birthday.daysUntil)
                    }}</small>
                  </div>
                } @empty {
                  <div class="vc-dashboard__empty">
                    {{ "DASHBOARD_OVERVIEW.BIRTHDAYS.EMPTY" | translate }}
                  </div>
                }
              </div>
              @if (birthdaysTotalPages > 1) {
                <footer class="vc-dashboard__compact-pagination">
                  <button type="button" [disabled]="birthdaysPage <= 1" (click)="changeBirthdaysPage(birthdaysPage - 1)"><i class="pi pi-angle-left"></i></button>
                  <span>{{ "DASHBOARD_OVERVIEW.BIRTHDAYS.PAGE" | translate : { current: birthdaysPage, total: birthdaysTotalPages } }}</span>
                  <button type="button" [disabled]="birthdaysPage >= birthdaysTotalPages" (click)="changeBirthdaysPage(birthdaysPage + 1)"><i class="pi pi-angle-right"></i></button>
                </footer>
              }
            </article>
          </section>
        }
      </div>
    </section>
  </main>
</div>
